import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ColDef, GetRowIdParams, GridReadyEvent } from 'ag-grid-community';
import { DeclineCategoryRow, DeclineReasonMappingCell, ToastType } from './interfaces/decline-reason-mapping.interface';
import { DeclineReasonMappingService } from './services/decline-reason-mapping.service';
import { DeclineMappingGridContext, DeclineMappingGridHostApi } from './interfaces/decline-mapping-grid-host.interface';
import { DeclineMappingStateService } from './services/decline-mapping-state.service';
import { DeclineMappingGridCoordinatorService } from './services/decline-mapping-grid-coordinator.service';

@Component({
  selector: 'decline-reason-mapping',
  templateUrl: './decline-reason-mapping.component.html',
  styleUrls: ['./decline-reason-mapping.component.scss'],
  providers: [DeclineMappingStateService, DeclineMappingGridCoordinatorService],
})
export class DeclineReasonMappingComponent implements OnInit, OnDestroy, DeclineMappingGridHostApi {
  loading = true;
  loadError: string | null = null;
  saving = false;
  toastMessage = '';
  toastType: ToastType = 'info';
  confirmDeleteRowId: string | null = null;
  columnDefs: ColDef<DeclineCategoryRow>[] = [];

  readonly defaultColDef: ColDef<DeclineCategoryRow> = {
    sortable: false,
    filter: false,
    resizable: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
  };

  gridContext!: DeclineMappingGridContext;

  readonly getRowId = (params: GetRowIdParams<DeclineCategoryRow>) => String(params.data?.id ?? '');

  private readonly destroy$ = new Subject<void>();
  private readonly mobileMq = window.matchMedia('(max-width: 575px)');
  private mobileLayout = this.mobileMq.matches;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    readonly state: DeclineMappingStateService,
    private grid: DeclineMappingGridCoordinatorService,
    private mappingService: DeclineReasonMappingService,
  ) {}

  // ---------------------------------------------------------------------------
  // DeclineMappingGridHostApi – delegates to services so cell renderers can
  // call back into the host through the grid context without coupling to the
  // component class directly.
  // ---------------------------------------------------------------------------

  get editingRowId(): string | null {
    return this.state.editingRowId;
  }

  get editDraft(): string {
    return this.state.editDraft;
  }

  setEditDraft(newDraft: string): void {
    this.state.editDraft = newDraft;
  }

  rowMappedCount(categoryRow: DeclineCategoryRow): number {
    return this.state.rowMappedCount(categoryRow);
  }

  refreshMappingCells(): void {
    this.grid.refreshAllCells();
  }

  isCellActive(rowId: string, portalId: string): boolean {
    return this.state.isCellActive(rowId, portalId);
  }

  removeReason(categoryRow: DeclineCategoryRow, portalId: string, reason: string): void {
    this.state.removeReason(categoryRow, portalId, reason);
    this.grid.refreshRowCells([categoryRow.id], [portalId]);
  }

  startEdit(categoryRow: DeclineCategoryRow): void {
    const previousEditingRowId = this.state.editingRowId;
    this.state.startEdit(categoryRow);
    this.grid.refreshRowCells(
      [categoryRow.id, previousEditingRowId].filter((maybeRowId): maybeRowId is string => !!maybeRowId),
    );
  }

  commitEdit(explicit = false): void {
    const trimmedName = this.state.editDraft.trim();
    if (!this.state.editingRowId) {
      this.state.clearEditing();
      this.grid.refreshAllCells();
      return;
    }
    if (!trimmedName) {
      if (explicit) {
        this.showToast('Category name cannot be empty', 'error');
      }
      const editingRowId = this.state.editingRowId;
      const categoryRow = this.state.getRowById(editingRowId);
      if (categoryRow?.id.startsWith('temp_')) {
        this.state.cleanupRowLocally(editingRowId);
        this.grid.removeRow(editingRowId);
      } else {
        this.state.clearEditing();
        this.grid.refreshAllCells();
      }
      return;
    }

    const categoryRow = this.state.getRowById(this.state.editingRowId);
    if (!categoryRow) {
      this.state.clearEditing();
      return;
    }

    const payload = { name: trimmedName };

    if (categoryRow.id.startsWith('temp_')) {
      const tempRowId = categoryRow.id;
      this.mappingService.createCategory(payload).pipe(takeUntil(this.destroy$)).subscribe({
        next: (createdCategory) => {
          if (!createdCategory._id) {
            this.showToast('Failed to create category', 'error');
            this.state.cleanupRowLocally(tempRowId);
            this.grid.removeRow(tempRowId);
            return;
          }
          this.state.promoteRow(tempRowId, createdCategory);
          this.grid.removeRow(tempRowId);
          const promotedCategoryRow = this.state.getRowById(createdCategory._id);
          if (promotedCategoryRow) this.grid.addRow(promotedCategoryRow);
          this.showToast('Category created', 'success');
        },
        error: (err: HttpErrorResponse) => {
          this.showToast(err.status === 409 ? 'A category with that name already exists' : 'Failed to create category', 'error');
          this.state.removeRowByRef(categoryRow);
          this.state.clearEditing();
          this.grid.removeRow(categoryRow.id);
        },
      });
    } else {
      this.mappingService.updateCategory(categoryRow.id, payload).pipe(takeUntil(this.destroy$)).subscribe({
        next: (updatedCategory) => {
          this.state.updateRowAfterEdit(categoryRow, updatedCategory);
          this.showToast('Category updated', 'success');
          this.grid.refreshRowCells([categoryRow.id]);
        },
        error: (err: HttpErrorResponse) => {
          this.showToast(err.status === 409 ? 'A category with that name already exists' : 'Failed to update category', 'error');
          this.state.clearEditing();
          this.grid.refreshRowCells([categoryRow.id]);
        },
      });
    }
  }

  cancelEdit(): void {
    const editingRowId = this.state.editingRowId;
    const categoryRow = editingRowId ? this.state.getRowById(editingRowId) : undefined;
    if (categoryRow?.id.startsWith('temp_')) {
      this.state.cleanupRowLocally(categoryRow.id);
      this.grid.removeRow(categoryRow.id);
    } else {
      this.state.clearEditing();
      this.grid.refreshRowCells(editingRowId ? [editingRowId] : []);
    }
  }

  onEditKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.commitEdit(true);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      this.cancelEdit();
    }
  }

  requestDeleteConfirm(rowId: string): void {
    this.confirmDeleteRowId = rowId;
  }

  cancelDeleteConfirm(): void {
    this.confirmDeleteRowId = null;
  }

  confirmDelete(): void {
    if (!this.confirmDeleteRowId) {
      return;
    }
    const rowId = this.confirmDeleteRowId;
    this.confirmDeleteRowId = null;
    this.deleteRow(rowId);
  }

  deleteRow(rowId: string): void {
    if (rowId.startsWith('temp_')) {
      this.state.cleanupRowLocally(rowId);
      this.grid.removeRow(rowId);
      return;
    }
    this.mappingService.deleteCategory(rowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.state.cleanupRowLocally(rowId);
        this.grid.removeRow(rowId);
        this.showToast('Category deleted', 'success');
      },
      error: (err: HttpErrorResponse) => {
        this.showToast(err.status === 409 ? 'Category is in use and cannot be deleted' : 'Failed to delete category', 'error');
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.gridContext = { host: this };
    this.mobileMq.addEventListener('change', this.onMobileLayoutChange);
    this.mappingService.loadAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: (loadedData) => {
        this.state.initialize(loadedData);
        this.applyColumnDefs();
        this.loading = false;
      },
      error: () => {
        this.loadError = 'Could not load decline reason mapping.';
        this.loading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.mobileMq.removeEventListener('change', this.onMobileLayoutChange);
    this.destroy$.next();
    this.destroy$.complete();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  private readonly onMobileLayoutChange = (): void => {
    const next = this.mobileMq.matches;
    if (next === this.mobileLayout) {
      return;
    }
    this.mobileLayout = next;
    if (!this.loading && !this.loadError && this.state.portals.length) {
      this.applyColumnDefs();
    }
  };

  private applyColumnDefs(): void {
    this.columnDefs = this.grid.buildColumnDefs(this.state.portals, this.mobileLayout);
    this.grid.setColumnDefs(this.columnDefs);
  }

  // ---------------------------------------------------------------------------
  // Grid events
  // ---------------------------------------------------------------------------

  onGridReady(ev: GridReadyEvent<DeclineCategoryRow>): void {
    this.grid.onGridReady(ev);
    this.grid.setInitialRows(this.state.rows);
  }

  // ---------------------------------------------------------------------------
  // Cell / panel interaction
  // ---------------------------------------------------------------------------

  clickCell(rowId: string, portalId: string): void {
    const previousCell = this.state.activeCell;
    const newCell: DeclineReasonMappingCell | null = this.state.isCellActive(rowId, portalId)
      ? null
      : { rowId, portalId };
    this.state.setActiveCell(newCell);
    this.grid.refreshPortalCells([previousCell, this.state.activeCell]);
  }

  closePanel(): void {
    const previousCell = this.state.activeCell;
    this.state.setActiveCell(null);
    this.grid.refreshPortalCells([previousCell]);
  }

  onChipClick(reason: string): void {
    const { activeCell, activeTakenSet } = this.state;
    if (!activeCell || activeTakenSet.has(reason)) return;
    this.state.toggleReason(activeCell.rowId, activeCell.portalId, reason);
    this.grid.refreshRowCells([activeCell.rowId], [activeCell.portalId]);
  }

  chipTitle(reason: string): string | null {
    const takenBy = this.state.activeTakenByReason[reason];
    return takenBy ? `Mapped to: ${takenBy}` : null;
  }

  // ---------------------------------------------------------------------------
  // Row management
  // ---------------------------------------------------------------------------

  addRow(): void {
    const previousCell = this.state.activeCell;
    const newCategoryRow = this.state.addTempRow();
    this.grid.refreshPortalCells([previousCell]);
    this.grid.addRow(newCategoryRow);
  }

  // ---------------------------------------------------------------------------
  // Save mappings
  // ---------------------------------------------------------------------------

  save(): void {
    const payload = this.state.computeMappingDiff();
    if (!payload.added?.length && !payload.removed?.length) {
      this.showToast('No mapping changes to save', 'info');
      return;
    }
    this.saving = true;
    this.mappingService.batchUpdateSettings(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.state.applyMappingSnapshot();
        this.showToast('Mapping saved', 'success');
      },
      error: () => {
        this.saving = false;
        this.showToast('Save failed', 'error');
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Miscellaneous
  // ---------------------------------------------------------------------------

  private showToast(message: string, type: ToastType = 'info'): void {
    this.toastMessage = message;
    this.toastType = type;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastMessage = '';
      this.toastTimer = null;
    }, 3000);
  }
}
